import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Button,
} from '@mui/material';
import CheckCircleIcon from 'mdi-react/CheckCircleIcon';
import AlertCircleIcon from 'mdi-react/AlertCircleIcon';
import ClockOutlineIcon from 'mdi-react/ClockOutlineIcon';
import PlayCircleOutlineIcon from 'mdi-react/PlayCircleOutlineIcon';
import PlayIcon from 'mdi-react/PlayIcon';
import React, { useState } from 'react';
import moment from 'moment';
import {
  useListJobsQuery,
  useGetRunningJobsQuery,
  useGetFailuresQuery,
} from 'store/app/entities/BatchSlice';
import TriggerJobDialog from './TriggerJobDialog';
import { I18n } from 'utils/i18n';

const MetricCard = ({ title, value, icon, color = 'primary' }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4">{value}</Typography>
        </Box>
        <Box sx={{ color: `${color}.main` }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

const StatusChip = ({ status }) => {
  const statusConfig = {
    running: { color: 'primary', label: 'Running' },
    queued: { color: 'warning', label: 'Queued' },
    idle: { color: 'default', label: 'Idle' },
    success: { color: 'success', label: 'Success' },
    failure: { color: 'error', label: 'Failed' },
  };
  const config = statusConfig[status] || statusConfig.idle;
  return <Chip label={config.label} color={config.color} size="small" variant="soft" />;
};

const OverviewDashboard = () => {
  const [isTriggerDialogOpen, setIsTriggerDialogOpen] = useState(false);
  const { data: jobsData, isLoading: jobsLoading } = useListJobsQuery();
  const { data: runningData } = useGetRunningJobsQuery();
  const { data: failuresData } = useGetFailuresQuery({
    days: 1,
    limit: 100,
  });

  // Note: WebSocket subscription is handled at parent level (AdminBatchScene)
  // RTK Query will automatically refetch when tags are invalidated

  // Calculate metrics
  const runningJobsCount = runningData?.running?.length || 0;
  const queuedJobsCount = runningData?.queueStats?.totalQueued || 0;
  const recentFailuresCount = failuresData?.failures?.length || 0;
  const successRate =
    jobsData?.jobs?.reduce((acc, job) => {
      const rate = job.successRate || 0;
      return acc + rate;
    }, 0) / (jobsData?.jobs?.length || 1) || 0;

  const jobs = jobsData?.jobs || [];

  return (
    <Box>
      <Grid container spacing={2} sx={{ marginBottom: 3 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <MetricCard
            title={I18n.t('AdminBatchScene.RunningJobs') || 'Running Jobs'}
            value={runningJobsCount}
            icon={<PlayCircleOutlineIcon size={40} />}
            color="primary"
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <MetricCard
            title={I18n.t('AdminBatchScene.QueuedJobs') || 'Queued Jobs'}
            value={queuedJobsCount}
            icon={<ClockOutlineIcon size={40} />}
            color="warning"
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <MetricCard
            title={I18n.t('AdminBatchScene.RecentFailures') || 'Failures (24h)'}
            value={recentFailuresCount}
            icon={<AlertCircleIcon size={40} />}
            color="error"
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <MetricCard
            title={I18n.t('AdminBatchScene.SuccessRate') || 'Success Rate (7d)'}
            value={`${successRate.toFixed(1)}%`}
            icon={<CheckCircleIcon size={40} />}
            color="success"
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">{I18n.t('AdminBatchScene.AllJobs') || 'All Jobs'}</Typography>
        <Button
          variant="contained"
          startIcon={<PlayIcon />}
          onClick={() => setIsTriggerDialogOpen(true)}
        >
          {I18n.t('AdminBatchScene.TriggerJob') || 'Trigger Job'}
        </Button>
      </Box>
      {jobsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>{I18n.t('AdminBatchScene.JobName') || 'Job Name'}</strong>
                </TableCell>
                <TableCell>
                  <strong>{I18n.t('AdminBatchScene.Status') || 'Status'}</strong>
                </TableCell>
                <TableCell>
                  <strong>{I18n.t('AdminBatchScene.LastExecution') || 'Last Execution'}</strong>
                </TableCell>
                <TableCell>
                  <strong>{I18n.t('AdminBatchScene.SuccessRate') || 'Success Rate'}</strong>
                </TableCell>
                <TableCell>
                  <strong>{I18n.t('AdminBatchScene.AvgDuration') || 'Avg Duration'}</strong>
                </TableCell>
                <TableCell>
                  <strong>{I18n.t('AdminBatchScene.Schedule') || 'Schedule'}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map(job => (
                <TableRow key={job.jobName} hover>
                  <TableCell>{job.jobName}</TableCell>
                  <TableCell>
                    <StatusChip
                      status={
                        runningData?.running?.find(r => r.jobName === job.jobName)
                          ? 'running'
                          : 'idle'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {job.lastExecutedAt
                      ? moment(job.lastExecutedAt).format('YYYY-MM-DD HH:mm:ss')
                      : 'Never'}
                  </TableCell>
                  <TableCell>{job.successRate?.toFixed(1) || 0}%</TableCell>
                  <TableCell>
                    {job.avgDuration ? `${(job.avgDuration / 1000).toFixed(1)}s` : '-'}
                  </TableCell>
                  <TableCell>{job.cron || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TriggerJobDialog
        isOpen={isTriggerDialogOpen}
        onClose={() => setIsTriggerDialogOpen(false)}
      />
    </Box>
  );
};

export default OverviewDashboard;
